import { memo, useEffect } from 'react';

import {
  DNTCaptcha,
  DNTCaptchaRealm,
  type DNTCaptchaRef,
  HookForm,
  Password,
  SelectedLoginUser,
  Text
} from '@dec/shared';
import { LanguageSelector } from 'app/components/translate/LanguageSelector';
import Env from 'env';
import queryString from 'query-string';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { ArrowRight, Book, Calculator, Leaf, WrenchAdjustable } from 'react-bootstrap-icons';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';

import validations from '../../components/login.validations';
import validationsUsername from '../../components/loginUsername.validations';
import { type LoginRequest } from '../../models/login.model';

import { useLogin } from './useLogin';

interface Props {}

interface SelectedUsernameProps {
  backLabel: string;
  onBack: () => void;
}

function SelectedUsername({ backLabel, onBack }: SelectedUsernameProps) {
  const { clearErrors, control, setValue } = useFormContext<LoginRequest>();
  const username = useWatch({ control, name: 'username' }) || '';

  const handleBack = () => {
    setValue('password', '');
    clearErrors('password');
    onBack();
  };

  return <SelectedLoginUser username={username} onBack={handleBack} backLabel={backLabel} />;
}

// eslint-disable-next-line react/display-name
export const Login = memo((props: Props) => {
  const {
    loading,
    usernameIsValid,
    validateCaptcha,
    externalWsLoginEnabled,
    externalWsLoginSelected,
    loginUserName,
    login,
    loadExternalWsLoginEnabled,
    resetUsername,
    setExternalWsLoginSelected
  } = useLogin();

  let dntCaptcha: DNTCaptchaRef;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();

  const [url, setUrl] = useQueryParam('returnUrl', StringParam);
  const clientId = queryString.parse(url?.substring(url?.indexOf('?')) as string)?.client_id;

  const navigate = useNavigate();

  // Initialize external login config if needed
  useEffect(() => {
    if (clientId && clientId === 'dec-admin') {
      loadExternalWsLoginEnabled();
    }
  }, [clientId, loadExternalWsLoginEnabled]);

  const onSubmit = async (data: LoginRequest) => {
    const returnUrl = url as string;

    if (usernameIsValid) {
      // Login with password
      await login({
        ...data,
        returnUrl,
        ...(dntCaptcha ? (dntCaptcha.getResponse() as any) : {})
      });
      dntCaptcha && dntCaptcha.refresh();
    } else {
      // Validate username first
      await loginUserName(data.username, returnUrl);
    }
  };

  const RecoverPassword = () => {
    navigate(`/recovery-request`);
  };

  const selectExternalLogin = (event: any) => {
    event.preventDefault();
    setExternalWsLoginSelected(true);
  };

  const unselectExternalWsLogin = (event: any) => {
    event.preventDefault();
    setExternalWsLoginSelected(false);
  };

  const signUp = () => {
    window.location.href = `${Env.appUrl}/public/taxpayer-request`;
  };
  return (
    <>
      <div className="container-fluid">
        {/* min-vh-100 hace que el row tenga como mínimo el alto de la ventana, y
        el col-6 heredará esa altura, permitiendo que la imagen de fondo
        ocupe todo el ancho y alto de ese panel. */}
        <div className="row min-vh-100">
          <div className="col-12 col-sm-12 col-md-6 bg-light">
            <LanguageSelector />

            <div className="row align-items-center h-80 mt-3">
              <div className="col-12 col-sm-7 col-md-10 col-lg-9 col-xl-7 col-xxl-6 mx-auto">
                <Card className="px-0 px-sm-4 pt-2 pb-3 login-card">
                  <Card.Body>
                    <Row className="mt-3 mb-2 px-5 px-sm-5">
                      <Col className="d-block">
                        <div className="img-fluid mx-auto mb-2 loginLogo" />
                      </Col>
                    </Row>

                    <HookForm
                      validations={usernameIsValid ? validations : validationsUsername}
                      onSubmit={onSubmit}
                    >
                      <Row>
                        <Col>
                          {!usernameIsValid ? (
                            <Text name="username" label={t('security-login.label.username')} />
                          ) : (
                            <SelectedUsername
                              backLabel={t('common.label.back')}
                              onBack={resetUsername}
                            />
                          )}
                        </Col>
                      </Row>
                      {!usernameIsValid ? (
                        <></>
                      ) : (
                        <>
                          <Row>
                            <Col>
                              <Password
                                name="password"
                                label={t('security-login.label.password')}
                                autoFocus={usernameIsValid}
                              />
                            </Col>
                          </Row>
                          {validateCaptcha ? (
                            <Row>
                              <Col className="align-items-center">
                                <DNTCaptcha
                                  realm={DNTCaptchaRealm.Security}
                                  dntCaptchaRef={(resp: DNTCaptchaRef) => (dntCaptcha = resp)}
                                  placeholder={t('security-login.label.captcha-placeholder')}
                                />
                              </Col>
                            </Row>
                          ) : (
                            <></>
                          )}
                        </>
                      )}
                      <Row className="mt-3">
                        <Col>
                          <Button type="submit" variant="primary" className="w-100">
                            {t(`security-login.label.${usernameIsValid ? 'loginButton' : 'next'}`)}
                          </Button>
                        </Col>
                      </Row>
                      {externalWsLoginEnabled && !externalWsLoginSelected && (
                        <Row>
                          <Col>
                            <div className="text-center mt-3">
                              <Link to="#" onClick={selectExternalLogin}>
                                {t('security-login.label.externalLogin')}
                              </Link>
                            </div>
                          </Col>
                        </Row>
                      )}

                      {externalWsLoginSelected && (
                        <Row className="mt-2">
                          <Col>
                            <Button onClick={unselectExternalWsLogin} variant={'secondary'}>
                              {t(`common.label.back`)}
                            </Button>
                          </Col>
                        </Row>
                      )}

                      {!externalWsLoginSelected && (
                        <Row>
                          <Col>
                            <div className="text-center mt-3">
                              <Link to="#" onClick={RecoverPassword}>
                                {t('security-login.label.passwordRecovery')}
                              </Link>
                            </div>
                          </Col>
                        </Row>
                      )}

                      <Row className="mt-5 mb-2 px-5 px-sm-5">
                        <Col className="d-block">
                          <div className="img-fluid mx-auto institutionLogo" />
                        </Col>
                      </Row>
                    </HookForm>
                  </Card.Body>
                </Card>
                <Card className="mt-3 px-4 login-card">
                  <Card.Body>
                    <span className="me-2"> {t('security-login.label.notRegistered')}</span>
                    <Link to="#" onClick={signUp}>
                      {t('security-login.label.signup')}
                    </Link>
                  </Card.Body>
                </Card>
              </div>
            </div>
          </div>
          <div className="col-12 col-sm-12 col-md-6 bg-light login-right px-sm-5">
            <div className="hero">
              <div className="hero-content">
                <h1>
                  {t('login.hero.title')}

                  <span className="text-primary"> {t('login.hero.name')}</span>
                </h1>

                <div className="d-flex align-items-start gap-3 mt-5">
                  <div>
                    <Leaf size={35} className="text-primary" />
                  </div>
                  <div>
                    <div className="fw-bolder text-primary"> {t('login.block1.title')}</div>
                    <div className="text-dark">{t('login.block1.text')}</div>
                  </div>
                </div>

                <div className="d-flex align-items-start gap-3 mt-5">
                  <div>
                    <WrenchAdjustable size={35} className="text-primary" />
                  </div>
                  <div>
                    <div className="fw-bolder text-primary"> {t('login.block2.title')}</div>
                    <div className="text-dark">{t('login.block2.text')}</div>
                  </div>
                </div>

                <div className="d-flex align-items-start gap-3 mt-5">
                  <div>
                    <Calculator size={35} className="text-primary" />
                  </div>
                  <div>
                    <div className="fw-bolder text-primary"> {t('login.block3.title')}</div>
                    <div className="text-dark">{t('login.block3.text')}</div>
                  </div>
                </div>

                <p>{t('login.links.title')}</p>
                <ul>
                  <li>
                    <a href="https://ciat.org/dec/" target="_blank" rel="noreferrer">
                      <Book className="text-primary" /> {t('login.links.link1')}
                      <ArrowRight className="ml-4" />
                    </a>
                  </li>
                  <li>
                    <a href="https://ciat.org/" target="_blank" rel="noreferrer">
                      <Book className="text-primary" /> {t('login.links.link2')}{' '}
                      <ArrowRight className="ml-4" />
                    </a>
                  </li>
                  <li>
                    <a href="https://pitaa.org/" target="_blank" rel="noreferrer">
                      <Book className="text-primary" /> {t('login.links.link3')}{' '}
                      <ArrowRight className="ml-4" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});
